/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {translate} from '@ion/core/src/locale/locale';
import {Observer} from '@ion/core/src/network/types';
import {GenericObject} from '@ion/core/src/types/entities';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {NotificationConnectivityIcon} from '@ion/dls/src/icons/NotificationConnectivityIcon';
import {NotificationDisconnectIcon} from '@ion/dls/src/icons/NotificationDisconnectIcon';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useNetworkStatus} from '../hooks/useNetworkStatus';

/**
 * Defines the NetworkStatusPrompt prop types, which accepts the following props:
 *
 * @see {@link Observer}
 *
 * @param onDismiss (Optional) function to be invoked when the user dismisses the prompt
 * @param onOffline (Optional) function to be invoked when the application goes offline.
 * @param onOnline (Optional) function to be invoked when the application goes back online.
 */
export interface NetworkStatusPromptProps {
  onAction?: () => void
  onOffline?: Observer;
  onOnline?: Observer;
}

/**
 * Defines the NetworkStatusPrompt header icon component which depicts the current
 * network connection state.
 */
export const NetworkStatusIcon = React.memo(({isOnline}: {isOnline: boolean}) => (
  <div style={{display: 'inline-block', marginTop: 5}}>
    {!isOnline
      ? <NotificationDisconnectIcon color={dlsColor('caRed')}/> : <NotificationConnectivityIcon />
    }
  </div>
));

/**
 * @private
 *
 * Provides the default component implementation prior to being wrapped in higher-order
 * components and exported.
 *
 * @see {@link NetworkStatusPrompt}
 */
const Prompt = ({onAction, onOffline, onOnline}: NetworkStatusPromptProps) => {
  const [show, setShow] = useState<boolean>(false);
  const {isDesktop} = useMedia();

  const onNetworkStatusChange = useCallback((isConnected: boolean) => {
    isConnected ? onOnline?.(isConnected) : onOffline?.(isConnected);
    setShow(true);
  }, [onOffline, onOnline]);

  const isOnline: boolean = useNetworkStatus(onNetworkStatusChange, null, true);

  const onClose = useCallback(() => {
    onAction?.();
    if (isOnline && isDesktop) {
      history.go();
    }
    setShow(false);
  }, [isOnline, onAction]);

  const labels: GenericObject = useMemo(() => (
    translate(`network:${!isOnline ? 'offline' : 'online'}`, {returnObjects: true})
  ), [isOnline]);

  return show ? (
    <UserNotificationDialog show
      icon={<NetworkStatusIcon isOnline={isOnline} />}
      title={labels.title}
      message={labels.message}
      actionLabel={(
        isOnline && !isDesktop ? translate('common:close') : labels.action
      )}
      onAction={onClose} />
  ) : null;
};

/**
 * The `NetworkStatusPrompt` provides the standard User Notification Component which is
 * displayed when the User's Network Status changes; i.e. when they're connection goes
 * `offline`, and / or back `online`.
 *
 * ```typescript
 *   import {NetworkStatusPrompt} from '@ion/applications/src/prompts/NetworkStatusPrompt';
 *
 *  // default implementation (deferred functionality ..)
 *  <NetworkStatusPrompt />
 *
 *  // custom implementation (handle prompt close ...)
 *  <NetworkStatusPrompt onDismiss={onClose} />
 *
 *  // custom implementation (handle prompt close, and network status changes ...)
 *  <NetworkStatusPrompt onDismiss={onClose}
 *   onOffline={onOffline}
 *   onOnline={onOnline} />
 * ```
 *
 * The `NetworkStatusPrompt` should only ever be rendered once (generally, via an
 * applications `_app.tsx` module).
 *
 * ```typescript
 *   <>
 *     <PageViewportMeta />
 *       {(theme && initialized) && (
 *         <I18nextProvider i18n={i18nMemo}>
 *           <ThemeProvider theme={theme}>
 *             <QueryClientProvider client={getQueryClient()}>
 *             ...
 *             </QueryClientProvider>
 *           </ThemeProvider>
 *         </I18nextProvider>
 *       )}
 *       {(theme && isMounted && !initialized) && (
 *         <ThemeProvider theme={theme}>
 *           <ContentLoader width='60px' height='60px' fullPage />
 *         </ThemeProvider>
 *       )}
 *      <NetworkStatusPrompt />
 *   </>
 * ```
 *
 * The following translation entries are required and must be defined via in applications
 * `public/network.json` translations document:
 *
 * ```json
 *   "network": {
 *     "offline": {
 *       "title": "...",
 *       "message": "...",
 *       "action": "..."
 *     },
 *     "online": {
 *       "title": "...",
 *       "message": "..",
 *       "action": "..."
 *     }
 *   }
 * ```
 *
 * @see {@link NetworkStatusPromptProps}
 * @see {@link useNetworkStatus}
 * @see {@link Observer}
 */
export const NetworkStatusPrompt = React.memo(
  withTranslation(['network'])(
    Prompt
  ));

export default NetworkStatusPrompt;
